/**
 * The Measurability panel's view model. FEAT-ATTRIB-003 §6 `S2`.
 *
 * `DA-BUG-ATTR-0263` found that §6 promises four display surfaces and §11
 * sequences two: this panel and the Boards tile were named, never put in a
 * phase, and then marked complete on another table's evidence. This is the
 * first of the two.
 *
 * It answers Q8 — *"can this tenant even be measured?"* — which **no
 * competitor publishes**, and it discharges MRC §2.3.4.3's "minimum data
 * sample sizes" requirement as a product surface rather than as an opinion in
 * a document.
 *
 * ## The one rule, inherited from `impactView.js`
 *
 * Nothing here computes a statistic. Every MDE, every required population and
 * every recommended holdout is READ from the field the service published. The
 * failure mode for this panel is not a crash — it is a holdout percentage that
 * looks sensible and is not the one the arithmetic supports, and the shortest
 * path to that is a client-side fallback when the service says `null`.
 */

import { count, share } from './impactView';

export const MEASURABILITY_STATE = Object.freeze({
    NOT_CONFIGURED: 'not_configured',
    UNAVAILABLE: 'unavailable',
    READY: 'ready',
});

/** A percentage the service already expressed as a percentage, not a ratio. */
export function pct(n) {
    if (n === null || n === undefined) return '—';
    const v = Number(n);
    if (typeof n !== 'number' || !Number.isFinite(v)) return '—';
    return `${v}%`;
}

/**
 * The payload into everything the panel displays.
 *
 * @param {object|null} payload   what `fetchReadiness` returned
 * @param {boolean} configured    whether the service has a base URL
 */
export function buildMeasurabilityView(payload, { configured = true } = {}) {
    if (!configured) {
        return {
            state: MEASURABILITY_STATE.NOT_CONFIGURED,
            envVar: 'REACT_APP_ATTRIBUTION_API_URL',
        };
    }

    if (!payload || payload.ok === false) {
        return {
            state: MEASURABILITY_STATE.UNAVAILABLE,
            reason: payload?.error ?? 'request_failed',
            message: payload?.message || payload?.detail || 'The service did not return a profile.',
        };
    }

    const d = payload.data ?? payload ?? {};
    const m = d.lift_measurability;

    /**
     * `null` means the at-risk denominator was not supplied — which the
     * service's own comment insists must NEVER be read as "the tenant is
     * measurable". `degraded` carries `lift_measurability_unavailable`.
     */
    if (!m || typeof m !== 'object') {
        return {
            state: MEASURABILITY_STATE.UNAVAILABLE,
            reason: 'lift_measurability_unavailable',
            message: 'The at-risk population could not be read, so no holdout can be sized.',
            degraded: Array.isArray(d.degraded) ? d.degraded : [],
        };
    }

    const assumptions = m.assumptions ?? {};

    return {
        state: MEASURABILITY_STATE.READY,

        /** `measurable` · `underpowered` · `refused`, from the service. */
        verdict: m.verdict ?? 'unknown',
        /**
         * Present only on a REFUSED verdict, and then it is the whole answer.
         * The plan's acceptance for `P0.2` is explicit: a zero-touch tenant
         * returns `no_sends_in_the_window`, **not `0`**.
         */
        refusalReason: m.reason ?? null,

        subjects: count(m.subjects),
        /**
         * The experiment's DENOMINATOR, and not the same thing as `subjects`.
         * A holdout randomises the subjects who would be messaged, so a tenant
         * where a sliver of its at-risk population is ever messaged has a far
         * smaller experiment than its at-risk count suggests.
         */
        experimentUnits: count(m.experiment_units),
        touchCoverage: m.touch_coverage
            ? `${count(m.touch_coverage.numerator)} of ${count(m.touch_coverage.denominator)} (${share(m.touch_coverage.ratio)})`
            : '—',

        baseline: m.baseline_rate
            ? {
                rate: share(m.baseline_rate.ratio),
                /**
                 * Shown, because it is an ASSUMPTION the service flags rather
                 * than a detail. `touched_subjects` means the rate came from
                 * the messaged population, which is SELECTED and converts
                 * differently; `all_subjects` means it did not, and the
                 * reader is entitled to know which.
                 */
                basis: m.baseline_rate.basis || 'not stated',
                numerator: count(m.baseline_rate.numerator),
                denominator: count(m.baseline_rate.denominator),
            }
            : null,

        /**
         * One row per offered holdout, each figure READ. `mde_relative` is
         * never recomputed from `mde_absolute` and the baseline: the service
         * derived it from the two-proportion z-test and a second derivation
         * here is a second place to disagree.
         */
        holdouts: (Array.isArray(m.by_holdout) ? m.by_holdout : []).map((h) => ({
            key: String(h.holdout_pct),
            holdout: pct(h.holdout_pct),
            control: count(h.control),
            treatment: count(h.treatment),
            mdeRelative: share(h.mde_relative),
            mdeAbsolute: share(h.mde_absolute),
        })),

        /**
         * NULL is rendered as "none is adequate" and is NEVER replaced by the
         * best available row.
         *
         * This is the discriminating case for the whole module. On cid=1 the
         * service returns `null` with verdict `underpowered`, because no
         * offered holdout detects the lift that matters — and a panel that
         * fell back to the smallest MDE would print "50%" and read as a
         * recommendation to withhold from half the audience for an experiment
         * that still cannot resolve the effect.
         */
        recommendedHoldout: typeof m.recommended_holdout_pct === 'number'
            ? pct(m.recommended_holdout_pct)
            : null,

        mdeAtTenPct: share(m.mde_relative_at_10pct),

        /**
         * "You are short" without "you need this many" is not actionable —
         * the service computes this for exactly that reason whenever a tenant
         * is underpowered.
         */
        subjectsRequired: typeof m.subjects_required === 'number'
            ? count(m.subjects_required)
            : null,

        /** MRC §2.3.4.3 — the test and its parameters, disclosed. */
        assumptions: {
            alpha: typeof assumptions.alpha === 'number' ? assumptions.alpha : null,
            power: typeof assumptions.power === 'number' ? assumptions.power : null,
            test: assumptions.test || 'not stated',
            /**
             * Echoed back because the 5% default is a JUDGEMENT, and a tenant
             * who cares about a different lift should see which one was asked.
             */
            usefulRelativeLift: share(assumptions.useful_relative_lift),
        },

        /** `P0.4`'s external calibration, when the same call carried it. */
        pathVolume: d.path_volume && typeof d.path_volume === 'object'
            ? {
                verdict: d.path_volume.verdict ?? 'unknown',
                reason: d.path_volume.reason ?? null,
            }
            : null,

        degraded: Array.isArray(d.degraded) ? d.degraded : [],
    };
}
